import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import { api } from "@/lib/api"
import { formatDayLabel, formatDayTick } from "./week-label"

interface TooltipProps {
	active?: boolean
	payload?: Array<{ value: number }>
	label?: string
}

export function StickinessChart() {
	const query = api.analytics.activeUsersChart.useQuery({})
	const chartData = query.data?.chartData

	const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
		if (!active || !payload?.length) return null
		return (
			<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
					{label ? formatDayLabel(label) : ""}
				</p>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Stickiness: {payload[0]?.value ?? 0}%
				</p>
			</div>
		)
	}

	return (
		<div className="rounded-lg border p-4">
			<h3 className="mb-3 text-sm font-medium text-gray-800 dark:text-white">
				Stickiness (DAU/MAU)
			</h3>
			<div className="h-64">
				{chartData && !query.isLoading ? (
					<div className="h-full w-full [&_*]:outline-none">
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart data={chartData} margin={{ left: -20, right: 8 }}>
								<defs>
									<linearGradient
										id="stickinessFill"
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop offset="0%" stopColor="#4CB782" stopOpacity={0.3} />
										<stop offset="100%" stopColor="#4CB782" stopOpacity={0} />
									</linearGradient>
								</defs>
								<CartesianGrid
									className="stroke-gray-200 dark:stroke-gray-700"
									vertical={false}
								/>
								<XAxis
									dataKey="date"
									tickFormatter={formatDayTick}
									minTickGap={48}
									className="text-xs text-gray-500 dark:text-gray-400"
									tickLine={false}
									axisLine={{
										className: "stroke-gray-300 dark:stroke-gray-700",
									}}
								/>
								<YAxis
									className="text-xs text-gray-500 dark:text-gray-400"
									axisLine={false}
									tickLine={false}
									tickFormatter={(value: number) => `${Math.round(value)}%`}
								/>
								<Tooltip
									content={<CustomTooltip />}
									cursor={{ stroke: "rgba(0, 0, 0, 0.1)" }}
								/>
								<Area
									type="monotone"
									dataKey="stickiness"
									stroke="#4CB782"
									strokeWidth={2}
									fill="url(#stickinessFill)"
								/>
							</AreaChart>
						</ResponsiveContainer>
					</div>
				) : query.isError ? (
					<div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
						Could not load PostHog data.
					</div>
				) : (
					<div className="flex h-full animate-pulse items-end justify-between gap-2 px-6">
						{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
							<div
								key={i}
								className="w-full rounded-t bg-gray-200 dark:bg-gray-700"
								style={{ height: `${i * 6 + 20}%` }}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
